// Stress testing & risk analysis
export function runSingleScenario(portfolio, drop, correlations = {}) {
  if (!portfolio || !portfolio.holdings) {
    return {
      scenario: `${drop}% Drop`,
      originalValue: 0,
      stressedValue: 0,
      stressedNLV: portfolio?.cash || 0,
      stressedEquity: portfolio?.cash || 0,
      marginCall: false,
      equityRatio: 100
    };
  }
  
  const originalValue = portfolio.holdings.reduce((sum, holding) => 
    sum + (holding.quantity * holding.price), 0);
  
  // Apply correlations if provided, otherwise assume perfect correlation
  const stressedValue = portfolio.holdings.reduce((sum, holding) => {
    const correlation = correlations[holding.symbol] || 1.0;
    const effectiveDrop = drop * correlation;
    const stressedPrice = holding.price * (1 - effectiveDrop / 100);
    return sum + (holding.quantity * stressedPrice);
  }, 0);
  
  const stressedNLV = stressedValue + portfolio.cash;
  const stressedEquity = stressedNLV - portfolio.marginUsed;
  const marginCall = stressedEquity < (portfolio.marginUsed * 0.25); // 25% maintenance requirement
  const equityRatio = stressedNLV > 0 ? (stressedEquity / stressedNLV) * 100 : 0;
  
  return {
    scenario: `${drop}% Drop`,
    originalValue,
    stressedValue,
    stressedNLV,
    stressedEquity,
    marginCall,
    equityRatio,
    loss: originalValue - stressedValue,
    lossPercentage: originalValue > 0 ? ((originalValue - stressedValue) / originalValue) * 100 : 0
  };
}

export function calculateVaR(portfolio, confidence = 0.95, scenarios = [5, 10, 15, 20, 25, 30, 35, 40]) {
  const stressTests = scenarios.map(drop => runSingleScenario(portfolio, drop));
  const losses = stressTests.map(test => test.loss).sort((a, b) => b - a);
  
  const varIndex = Math.floor((1 - confidence) * losses.length);
  const var95 = losses[varIndex] || 0;
  const expectedShortfall = losses.slice(0, varIndex + 1).reduce((sum, loss) => sum + loss, 0) / (varIndex + 1) || 0;
  
  return {
    var95,
    expectedShortfall,
    scenarios: stressTests,
    worstCase: Math.max(...losses),
    confidence,
    marginCallThreshold: findMarginCallThreshold(portfolio)
  };
}

export function findMarginCallThreshold(portfolio) {
  if (!portfolio || !portfolio.holdings || portfolio.marginUsed <= 0) {
    return { threshold: 0, safe: true };
  }
  
  const currentValue = portfolio.holdings.reduce((sum, h) => sum + (h.quantity * h.price), 0);
  const requiredEquity = portfolio.marginUsed * 0.25; // 25% maintenance
  const requiredNLV = portfolio.marginUsed + requiredEquity;
  const requiredPortfolioValue = requiredNLV - portfolio.cash;
  
  if (requiredPortfolioValue <= 0 || currentValue <= 0) {
    return { threshold: 0, safe: true };
  }
  
  const thresholdDrop = Math.max(0, ((currentValue - requiredPortfolioValue) / currentValue) * 100);
  
  return {
    threshold: thresholdDrop,
    safe: thresholdDrop > 20, // Safe if can handle >20% drop
    requiredValue: requiredPortfolioValue,
    currentValue,
    marginBuffer: currentValue - requiredPortfolioValue
  };
}

export function runMonteCarloSimulation(portfolio, numSimulations = 1000, timeHorizon = 30, volatility = 0.2) {
  const results = [];
  const currentValue = portfolio.holdings.reduce((sum, h) => sum + (h.quantity * h.price), 0);
  
  for (let i = 0; i < numSimulations; i++) {
    let simulatedValue = currentValue;
    
    for (let day = 0; day < timeHorizon; day++) {
      const randomShock = generateRandomNormal() * volatility / Math.sqrt(252); // Daily volatility
      simulatedValue *= (1 + randomShock);
    }
    
    const finalNLV = simulatedValue + portfolio.cash;
    const finalEquity = finalNLV - portfolio.marginUsed;
    const marginCall = finalEquity < (portfolio.marginUsed * 0.25);
    
    results.push({
      finalValue: simulatedValue,
      finalNLV,
      finalEquity,
      marginCall,
      return: ((simulatedValue - currentValue) / currentValue) * 100
    });
  }
  
  // Calculate statistics
  const returns = results.map(r => r.return).sort((a, b) => a - b);
  const marginCallProbability = (results.filter(r => r.marginCall).length / numSimulations) * 100;
  
  return {
    results,
    statistics: {
      meanReturn: returns.reduce((sum, r) => sum + r, 0) / returns.length,
      medianReturn: returns[Math.floor(returns.length / 2)],
      var95: returns[Math.floor(0.05 * returns.length)],
      var99: returns[Math.floor(0.01 * returns.length)],
      maxLoss: Math.min(...returns),
      maxGain: Math.max(...returns),
      marginCallProbability,
      probOfLoss: (returns.filter(r => r < 0).length / returns.length) * 100
    }
  };
}

export function analyzeCorrelations(portfolio, marketData = {}) {
  if (!portfolio.holdings || portfolio.holdings.length < 2) {
    return { correlations: {}, portfolioCorrelation: 0, diversificationBenefit: 0 };
  }
  
  const symbols = portfolio.holdings.map(h => h.symbol);
  const correlations = {};
  
  // Simplified correlation analysis (in practice, would use historical returns data)
  symbols.forEach(symbol1 => {
    correlations[symbol1] = {};
    symbols.forEach(symbol2 => {
      if (symbol1 === symbol2) {
        correlations[symbol1][symbol2] = 1.0;
      } else {
        // Simplified: assign correlations based on asset type
        correlations[symbol1][symbol2] = estimateCorrelation(symbol1, symbol2, marketData);
      }
    });
  });
  
  // Calculate weighted portfolio correlation
  const totalValue = portfolio.holdings.reduce((sum, h) => sum + (h.quantity * h.price), 0);
  const weights = portfolio.holdings.map(h => (h.quantity * h.price) / totalValue);
  
  let portfolioVariance = 0;
  for (let i = 0; i < weights.length; i++) {
    for (let j = 0; j < weights.length; j++) {
      const symbol1 = symbols[i];
      const symbol2 = symbols[j];
      const correlation = correlations[symbol1][symbol2];
      const vol1 = marketData[symbol1]?.volatility || 0.25;
      const vol2 = marketData[symbol2]?.volatility || 0.25;
      portfolioVariance += weights[i] * weights[j] * vol1 * vol2 * correlation;
    }
  }
  
  const portfolioVolatility = Math.sqrt(portfolioVariance);
  const weightedAvgVolatility = weights.reduce((sum, w, i) => {
    const vol = marketData[symbols[i]]?.volatility || 0.25;
    return sum + w * vol;
  }, 0);
  
  const diversificationBenefit = Math.max(0, ((weightedAvgVolatility - portfolioVolatility) / weightedAvgVolatility) * 100);
  
  return {
    correlations,
    portfolioVolatility,
    diversificationBenefit,
    riskReduction: diversificationBenefit
  };
}

function estimateCorrelation(symbol1, symbol2, marketData) {
  // Simplified correlation estimation
  const sector1 = getSector(symbol1);
  const sector2 = getSector(symbol2);
  
  if (sector1 === sector2) return 0.7; // Same sector
  if (sector1 === 'Tech' && sector2 === 'Tech') return 0.8;
  if (sector1 === 'Financial' && sector2 === 'Financial') return 0.75;
  if ((sector1 === 'ETF' && sector2 === 'Tech') || (sector1 === 'Tech' && sector2 === 'ETF')) return 0.85;
  return 0.6; // Different sectors
}

function getSector(symbol) {
  const techStocks = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'TSLA', 'NVDA'];
  const financialStocks = ['JPM', 'BAC', 'WFC', 'GS', 'MS'];
  const etfs = ['SPY', 'QQQ', 'IWM', 'VTI', 'VOO'];
  
  if (techStocks.includes(symbol)) return 'Tech';
  if (financialStocks.includes(symbol)) return 'Financial';
  if (etfs.includes(symbol)) return 'ETF';
  return 'Other';
}

function generateRandomNormal() {
  // Box-Muller transformation for normal distribution
  const u1 = Math.random();
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}